import { Gauge } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdoptionTab } from '@/components/internal/AdoptionTab';
import { BrainAdoptionTab } from '@/components/internal/BrainAdoptionTab';
import { TrafficTab } from '@/components/internal/TrafficTab';
import { AccessLogTab } from '@/components/internal/AccessLogTab';
import { HealthTab } from '@/components/internal/HealthTab';
import { TasksTab } from '@/components/internal/tasks/TasksTab';
import { SecurityTab } from '@/components/internal/SecurityTab';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/SharedAuthContext';

const TABS = ['adoption', 'brain', 'traffic', 'access', 'health', 'tasks', 'security'] as const;
/** Con el permiso extra subtab:internal:adoption (sin rol admin) solo se ven estas dos. */
const VIEWER_TABS: readonly (typeof TABS)[number][] = ['adoption', 'brain'];

/**
 * Panel de Internal — adopción, tráfico, accesos, salud y tareas del equipo.
 * Admin ve todo; quien entra por subtab:internal:adoption ve Adopción y Brain
 * (Sol 01/10/2026), sin la card de configuración de evals, que escribe.
 */
export function InternalPanelPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).includes('admin');
  const tabs: readonly (typeof TABS)[number][] = isAdmin ? TABS : VIEWER_TABS;
  // ?tab=security: destino del escape anti-lockout de MFA (MfaRequiredScreen).
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const defaultTab = (tabs as readonly string[]).includes(requested ?? '') ? (requested as string) : 'adoption';
  const show = (tab: (typeof TABS)[number]) => tabs.includes(tab);
  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/30 shrink-0 mt-0.5">
          <Gauge className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('internal.internalpanel.panel_de_internal')}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {t('internal.internalpanel.adopcion_trafico_accesos_salud_y_tareas')}
          </p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="adoption">{t('internal.internalpanel.adopcion')}</TabsTrigger>
          <TabsTrigger value="brain">Brain</TabsTrigger>
          {show('traffic') && <TabsTrigger value="traffic">{t('internal.internalpanel.trafico')}</TabsTrigger>}
          {show('access') && <TabsTrigger value="access">Accesos</TabsTrigger>}
          {show('health') && <TabsTrigger value="health">Salud</TabsTrigger>}
          {show('tasks') && <TabsTrigger value="tasks">Tareas</TabsTrigger>}
          {show('security') && <TabsTrigger value="security">{t('internal.securitytab.tab', 'Seguridad')}</TabsTrigger>}
        </TabsList>

        <TabsContent value="adoption"><AdoptionTab /></TabsContent>
        <TabsContent value="brain"><BrainAdoptionTab canManage={isAdmin} /></TabsContent>
        {show('traffic') && <TabsContent value="traffic"><TrafficTab /></TabsContent>}
        {show('access') && <TabsContent value="access"><AccessLogTab /></TabsContent>}
        {show('health') && <TabsContent value="health"><HealthTab /></TabsContent>}
        {show('tasks') && <TabsContent value="tasks"><TasksTab /></TabsContent>}
        {show('security') && <TabsContent value="security"><SecurityTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
